/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser'
import { AppSettings } from '../appSettings'

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

const authConfigs = {
  default: async () => {
    const ms_auth = await AppSettings.get('MS_AUTH')
    return {
      clientId: ms_auth['CLIENT_ID'],
      authority: ms_auth['AUTHORITY'],
      redirectUri: ms_auth['REDIRECT_URI']
    }
  },

  mfa: async () => {
    const ms_mfa_auth = await AppSettings.get('MS_MFA_AUTH')
    return {
      clientId: ms_mfa_auth['CLIENT_ID'],
      authority: ms_mfa_auth['AUTHORITY'],
      redirectUri: ms_mfa_auth['REDIRECT_URI']
    }
  }
}

export const getMsalConfig = async (configType = 'default') => {
  const authProvider = authConfigs[configType] || authConfigs.default
  const auth = await authProvider()

  return {
    auth,
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              return
            case LogLevel.Info:
              console.info(message)
              return
            case LogLevel.Verbose:
              console.debug(message)
              return
            case LogLevel.Warning:
              console.warn(message)
              return
          }
        }
      }
    }
  }
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['User.Read', 'Directory.Read.All', 'Mail.Read']
}

export const loginRequestApi = {
  scopes: ['https://management.azure.com/.default']
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me'
}
